import React, { useState, useEffect, useContext } from 'react';

import { Box } from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import { useParams, useLocation, useHistory } from 'react-router-dom';

import { DiscoverContext } from '../../../../context/DiscoverContext';
import { LearnerInfo } from '../../../../types';
import { useSERPNavigation } from '../../../../utils/hooks';
import { HITS_TYPES } from '../../const';
import { FiltersOverlay } from '../FiltersOverlay';
import FiltersPanel from '../FiltersPanel';
import ResultsPanel from '../ResultsPanel';
import useStyles from './styles';

type StateType = {
  query?: string;
  hierarchicalMenu?: any;
  refinementList?: any;
  menu?: any;
};

interface SERPContainerProps {
  width: Breakpoint;
  logoUrl?: string;
  userInfo?: LearnerInfo;
  tenantTitle?: string | null;
  tenantName?: string | null;
  linkedInId?: string | null;
  updateLocationOnClear?: boolean;
}

export const SERPContainer: React.FC<SERPContainerProps> = ({
  width,
  logoUrl,
  userInfo,
  tenantTitle,
  tenantName,
  linkedInId,
  updateLocationOnClear = false,
}) => {
  const classes = useStyles();
  const isMobile = !isWidthUp('xl', width);

  const params = useParams();
  const location = useLocation();
  const history = useHistory();
  const isCategoryPage = !!params?.category;
  const isExpertPage = !!params?.expertSlug;
  const serpType = useSERPNavigation();
  const isGlossaryPage = serpType === HITS_TYPES.GLOSSARY;

  const [canClearFilters, setCanClearFilters] = useState(false);
  const type = useSERPNavigation();
  const resetTypeFilter = isCategoryPage || type === '';

  const { setOpenFiltersPanel, setSearchState, searchState } = useContext(DiscoverContext);

  const handleResetFilters = () => {
    setSearchState((prevState) => {
      const newState: StateType = {
        query: prevState.query,
      };
      //save category filter if category page
      if (isCategoryPage) {
        newState.hierarchicalMenu = {
          ...prevState.hierarchicalMenu,
        };
      }

      //save tabs state if ResultsPanel not SERP ALL page
      if (prevState?.refinementList?.searchable_type && !resetTypeFilter) {
        newState.refinementList = {
          ...newState.refinementList,
          searchable_type: prevState?.refinementList?.searchable_type,
        };
      }
      if (prevState?.refinementList?.user && isExpertPage) {
        newState.refinementList = {
          ...newState.refinementList,
          user: prevState?.refinementList?.user,
        };
      }
      return newState;
    });
    setOpenFiltersPanel(false);
    if (updateLocationOnClear) {
      history.push(location.pathname);
    }
  };

  //check if possible to clear filters
  useEffect(() => {
    const { refinementList, hierarchicalMenu, menu } = searchState;

    if (hierarchicalMenu && !isCategoryPage) {
      setCanClearFilters(true);
    } else if (menu && isGlossaryPage) {
      setCanClearFilters(true);
    } else if (refinementList) {
      const obj = {};
      //clear empty filter values
      for (let key in refinementList) {
        if (refinementList.hasOwnProperty(key) && refinementList[key]) {
          obj[key] = refinementList[key];
        }
      }
      //check if has filters from panel
      const filters = Object.keys(obj).filter(
        (item) =>
          (resetTypeFilter || item !== 'searchable_type') && (!isExpertPage || item !== 'user'),
      );

      if (filters.length > 0) {
        setCanClearFilters(true);
      } else {
        setCanClearFilters(false);
      }
    } else {
      setCanClearFilters(false);
    }
  }, [searchState, isCategoryPage, resetTypeFilter, isExpertPage, isGlossaryPage]);

  return (
    <Box className={classes.wrapper}>
      <FiltersOverlay
        logoUrl={logoUrl}
        canClearFilters={canClearFilters}
        handleResetFilters={handleResetFilters}
      >
        <FiltersPanel
          isMobile={isMobile}
          resetTypeFilter={resetTypeFilter}
          isSearching={searchState?.query}
          tenantName={tenantName}
        />
      </FiltersOverlay>
      <ResultsPanel
        userInfo={userInfo}
        tenantTitle={tenantTitle}
        tenantName={tenantName}
        linkedInId={linkedInId}
      />
    </Box>
  );
};

export default withWidth()(SERPContainer);
